import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type QboAccountRow, type FpaDepartmentRow } from '../lib/api';
import {
  Card, CardHeader, Table, Th, Tr, Td, Badge, Select, ActionButton, Spinner, EmptyState,
} from '../components/ui';
import {
  lineCodeFamily, effectiveLineCode, FAMILY_LABEL, FAMILY_BADGE,
} from '../lib/fpaLineCodes';
import { LineCodeMapSelect, AccountDepartmentSelect } from '../components/LineCodeMapSelect';
import { fmtCurrencyWhole } from '../lib/fmt';

const cellInputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

/** Text cell that saves on blur (approver only); read-only otherwise. */
function EditableText({ value, placeholder, width, disabled, onSave }: {
  value: string; placeholder?: string; width?: number; disabled?: boolean; onSave: (next: string) => Promise<void>;
}) {
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value); }, [value]);
  if (disabled) return <span>{value || <span style={{ color: 'var(--color-text-muted)' }}>{placeholder ?? '—'}</span>}</span>;
  const save = async () => {
    const next = val.trim();
    if (next === value.trim()) return;
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); }
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        value={val} placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        style={{ ...cellInputStyle, width: width ?? 140 }}
      />
      {saving && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>saving…</span>}
    </span>
  );
}

/** Departments reference list (shared by QBO actuals AND the planning spine).
 *  Approvers can add / rename / reorder / retire; others see it read-only. */
function DepartmentsCard({ isApprover, departments, roster, onChanged }: {
  isApprover: boolean; departments: FpaDepartmentRow[]; roster: Map<string, string[]>; onChanged: () => void;
}) {
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newDiv, setNewDiv] = useState('');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    setErr(null);
    if (!newId.trim() || !newName.trim()) { setErr('id and name are required'); return; }
    setAdding(true);
    try {
      await api.fpa.createDepartment({
        id: newId.trim().toLowerCase(), name: newName.trim(),
        division: newDiv.trim() || null,
        sort_order: (departments.reduce((m, d) => Math.max(m, d.sort_order), 0) + 1),
      });
      setNewId(''); setNewName(''); setNewDiv(''); onChanged();
    } catch (e) { setErr((e as Error).message); }
    finally { setAdding(false); }
  }

  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Departments</div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          ORP departments used by Class → department and the planning spine{isApprover ? '' : ' (read-only — approver required to edit)'}
        </span>
      </CardHeader>
      {departments.length === 0 ? <EmptyState message="No departments defined." /> : (
        <Table>
          <thead><tr><Th>ID</Th><Th>Name</Th><Th>Division</Th><Th right>Staff</Th><Th>Order</Th><Th>Status</Th>{isApprover && <Th>Actions</Th>}</tr></thead>
          <tbody>
            {departments.map((d) => {
              const members = roster.get(d.id) ?? [];
              return (
              <Tr key={d.id}>
                <Td muted><code style={{ fontSize: 11 }}>{d.id}</code></Td>
                <Td bold><EditableText value={d.name} disabled={!isApprover} onSave={(v) => api.fpa.updateDepartment(d.id, { name: v }).then(onChanged)} /></Td>
                <Td><EditableText value={d.division ?? ''} placeholder="—" disabled={!isApprover} onSave={(v) => api.fpa.updateDepartment(d.id, { division: v || null }).then(onChanged)} /></Td>
                <Td right muted><span title={members.join(', ') || 'No staff mapped'}>{members.length}</span></Td>
                <Td muted>{d.sort_order}</Td>
                <Td>{d.active ? <Badge variant="green">active</Badge> : <Badge variant="gray">retired</Badge>}</Td>
                {isApprover && (
                  <Td>
                    <ActionButton onClick={() => api.fpa.updateDepartment(d.id, { active: !d.active }).then(onChanged)}>
                      {d.active ? 'Retire' : 'Reactivate'}
                    </ActionButton>
                  </Td>
                )}
              </Tr>
            ); })}
          </tbody>
        </Table>
      )}
      {isApprover && (
        <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 11, borderTop: '0.5px solid var(--color-border)' }}>
          <input value={newId} placeholder="id (slug)" onChange={(e) => setNewId(e.target.value)} style={{ ...cellInputStyle, width: 110 }} />
          <input value={newName} placeholder="name" onChange={(e) => setNewName(e.target.value)} style={{ ...cellInputStyle, width: 160 }} />
          <input value={newDiv} placeholder="division (optional)" onChange={(e) => setNewDiv(e.target.value)} style={{ ...cellInputStyle, width: 150 }} />
          <ActionButton variant="primary" onClick={add} disabled={adding}>{adding ? 'Adding…' : 'Add department'}</ActionButton>
          {err && <span style={{ color: 'var(--color-red-text)' }}>{err}</span>}
        </div>
      )}
    </Card>
  );
}

/** Resolved-effect cell: what the account actually rolls into right now. */
function EffectiveCell({ account }: { account: QboAccountRow }) {
  const eff = effectiveLineCode(account.line_code, account.classification);
  const fam = lineCodeFamily(eff);
  if (!eff || !fam) return <Badge variant="amber">unmapped — bucketed</Badge>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <Badge variant={FAMILY_BADGE[fam]}>{FAMILY_LABEL[fam]}</Badge>
      {!account.line_code && <span style={{ color: 'var(--color-text-muted)' }}>via classification</span>}
    </span>
  );
}

function HelpNote({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 16px', fontSize: 11, color: 'var(--color-text-muted)', background: 'var(--color-blue-bg)', borderBottom: '0.5px solid var(--color-border)', lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────────

type QboTab = 'connection' | 'accounts' | 'departments' | 'revenue';
const TAB_LABELS: Record<QboTab, string> = {
  connection: 'Connection',
  accounts: 'Accounts → lines',
  departments: 'Classes & departments',
  revenue: 'Revenue types',
};

function ConnectionTab({ isApprover }: { isApprover: boolean }) {
  const status = useQuery(() => api.fpa.qbo.status());
  const [connectMsg, setConnectMsg] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const s = status.data;
  async function connect() {
    setConnectMsg(null);
    try {
      const { authorize_url } = await api.fpa.qbo.connect();
      window.open(authorize_url, '_blank', 'noopener');
      setConnectMsg('Opened the QuickBooks authorization page in a new tab. Complete it there; the connection completes on redirect.');
    } catch (err) { setConnectMsg((err as Error).message); }
  }
  async function syncNow() {
    setSyncMsg(null); setSyncing(true);
    try {
      const r = await api.fpa.qbo.sync();
      setSyncMsg(r.status === 'synced'
        ? `Synced ${r.actuals_upserted} actuals rows${r.from ? ` for ${r.from}–${r.to}` : ''}. The Actuals and planning suggestions now reflect the latest QuickBooks figures.`
        : `Sync returned "${r.status}" — check the connection.`);
      status.refetch();
    } catch (err) { setSyncMsg((err as Error).message); }
    finally { setSyncing(false); }
  }
  return (
    <Card>
      <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>QuickBooks connection</div></CardHeader>
      {status.isLoading ? <Spinner /> : (
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge variant={s?.connected ? 'green' : s?.configured ? 'amber' : 'gray'}>
              {s?.connected ? 'Connected' : s?.configured ? 'Not connected' : 'Not configured'}
            </Badge>
            {s && <span style={{ color: 'var(--color-text-muted)' }}>
              {s.account_count} accounts · {s.class_count} classes · {s.actuals_periods} periods
              {s.last_synced_at ? ` · last synced ${new Date(s.last_synced_at).toLocaleString()}` : ' · never synced'}
            </span>}
          </div>
          {!s?.configured && (
            <div style={{ color: 'var(--color-text-muted)' }}>
              Set <code>QBO_CLIENT_ID</code> / <code>QBO_CLIENT_SECRET</code> (and the redirect URI) to enable the QBO integration.
            </div>
          )}
          {s?.configured && isApprover && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <ActionButton variant="primary" onClick={connect}>{s.connected ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}</ActionButton>
              {s.connected && <ActionButton onClick={syncNow} disabled={syncing}>{syncing ? 'Syncing…' : 'Sync QBO now'}</ActionButton>}
            </div>
          )}
          {s?.connected && isApprover && (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              Pulls a trailing 12 months of P&amp;L into ORP now, instead of waiting for the nightly full sync. Needed before actuals-based planning suggestions can use recent figures.
            </div>
          )}
          {connectMsg && <div style={{ color: 'var(--color-text-muted)' }}>{connectMsg}</div>}
          {syncMsg && <div style={{ color: 'var(--color-text-muted)' }}>{syncMsg}</div>}
        </div>
      )}
    </Card>
  );
}

// QBO classification ordering — P&L-sensible (revenue → COGS → expense),
// unknown classifications after the knowns, unclassified last.
const CLASSIFICATION_RANK: Record<string, number> = {
  Revenue: 0, Income: 1, CostOfGoodsSold: 2, Expense: 3, OtherIncome: 4, OtherExpense: 5,
};
const CLASSIFICATION_LABEL: Record<string, string> = {
  Revenue: 'Revenue', Income: 'Income', CostOfGoodsSold: 'Cost of Goods Sold',
  Expense: 'Expense', OtherIncome: 'Other Income', OtherExpense: 'Other Expense',
};
const classRank = (c: string | null) => (c == null ? 1000 : CLASSIFICATION_RANK[c] ?? 500);
const classLabel = (c: string | null) => (c == null ? 'Unclassified' : CLASSIFICATION_LABEL[c] ?? c);

/** Sort by classification (P&L order), then account type, then account name. */
function sortAccounts(rows: QboAccountRow[]): QboAccountRow[] {
  return [...rows].sort((a, b) =>
    classRank(a.classification) - classRank(b.classification)
    || (a.classification ?? '').localeCompare(b.classification ?? '')
    || (a.account_type ?? '').localeCompare(b.account_type ?? '')
    || a.name.localeCompare(b.name),
  );
}

function AccountsTab({ isApprover }: { isApprover: boolean }) {
  const accounts = useQuery(() => api.fpa.qbo.accounts());
  const depts = useQuery(() => api.fpa.departments());
  const deptList = depts.data?.departments ?? [];
  const rows = accounts.data?.accounts ?? [];
  const [gapsOnly, setGapsOnly] = useState(false);
  const isGap = (a: QboAccountRow) => !a.line_code; // not explicitly mapped (fallback or none)
  const explicit = rows.filter((a) => a.line_code).length;
  const viaFallback = rows.filter((a) => !a.line_code && effectiveLineCode(null, a.classification)).length;
  const unmapped = rows.filter((a) => !effectiveLineCode(a.line_code, a.classification)).length;
  const amtFallback = rows.filter((a) => !a.line_code && effectiveLineCode(null, a.classification)).reduce((s, a) => s + a.amount, 0);
  const amtUnmapped = rows.filter((a) => !effectiveLineCode(a.line_code, a.classification)).reduce((s, a) => s + a.amount, 0);
  // Default view groups by classification; "only gaps" flattens to the
  // unmapped/fallback accounts, biggest $ first (the punch-list).
  const display = gapsOnly
    ? rows.filter(isGap).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
    : sortAccounts(rows);

  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Accounts → FP&A lines</div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {rows.length} accounts · {explicit} mapped · {viaFallback} via classification ({fmtCurrencyWhole(amtFallback)}) · {unmapped} unmapped ({fmtCurrencyWhole(amtUnmapped)})
        </span>
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={() => setGapsOnly((v) => !v)}>{gapsOnly ? 'Show all' : 'Only gaps'}</ActionButton>
        </span>
      </CardHeader>
      <HelpNote>
        Each QuickBooks account rolls up to one FP&A P&L line. Pick the line from the dropdown — the
        groups (<b>Revenue</b>, <b>Labor</b>, <b>Variable</b>, <b>Fixed</b>) decide where it lands in
        margin &amp; variance. Leave it <b>Unmapped</b> to use the automatic fallback from the QBO
        classification; the <b>Effective</b> column shows what each account resolves to today. The
        <b> Department</b> column pins a department-specific account (e.g. “Salaries – Engineering”)
        straight to that department — it wins over the class dimension. <b>Actuals</b> is the trailing
        12 months, so you can prioritise the mappings that move real money — hit <b>Only gaps</b> for
        just the unmapped/fallback accounts.
      </HelpNote>
      {accounts.isLoading ? <Spinner />
        : rows.length === 0 ? <EmptyState message="No QBO accounts synced yet." />
        : display.length === 0 ? <EmptyState message="No gaps — every account is explicitly mapped." />
        : (
          <Table>
            <thead><tr><Th>Account</Th><Th>Type</Th><Th>Classification</Th><Th right>Actuals (12mo)</Th><Th>FP&A line</Th><Th>Department</Th><Th>Effective</Th></tr></thead>
            <tbody>
              {display.map((a, i) => {
                const showGroup = !gapsOnly && (i === 0 || a.classification !== display[i - 1].classification);
                return (
                  <Fragment key={a.qbo_account_id}>
                    {showGroup && (
                      <tr>
                        <td colSpan={7} style={{
                          padding: '6px 12px', fontSize: 10, fontWeight: 600, letterSpacing: '.05em',
                          textTransform: 'uppercase', color: 'var(--color-text-muted)',
                          background: 'var(--color-bg)', borderTop: '0.5px solid var(--color-border)',
                        }}>{classLabel(a.classification)}</td>
                      </tr>
                    )}
                    <Tr>
                      <Td bold>{a.name}{!a.active && <Badge variant="gray">inactive</Badge>}</Td>
                      <Td muted>{a.account_type ?? '—'}</Td>
                      <Td muted>{a.classification ?? '—'}</Td>
                      <Td right muted>{fmtCurrencyWhole(a.amount)}</Td>
                      <Td><LineCodeMapSelect qboAccountId={a.qbo_account_id} lineCode={a.line_code} classification={a.classification} disabled={!isApprover} onSaved={accounts.refetch} /></Td>
                      <Td><AccountDepartmentSelect qboAccountId={a.qbo_account_id} departmentId={a.department_id} departments={deptList} disabled={!isApprover} onSaved={accounts.refetch} /></Td>
                      <Td><EffectiveCell account={a} /></Td>
                    </Tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
    </Card>
  );
}

function DepartmentsTab({ isApprover }: { isApprover: boolean }) {
  const classes = useQuery(() => api.fpa.qbo.classes());
  const departments = useQuery(() => api.fpa.departments());
  const allDepartments = useQuery(() => api.fpa.departments(true));
  const rosterQ = useQuery(() => api.fpa.departmentRoster());
  const [classGapsOnly, setClassGapsOnly] = useState(false);
  const roster = new Map<string, string[]>();
  for (const m of rosterQ.data?.members ?? []) {
    const arr = roster.get(m.department_id) ?? [];
    arr.push(m.title ? `${m.display_name} · ${m.title}` : m.display_name);
    roster.set(m.department_id, arr);
  }
  return (
    <>
      {allDepartments.isLoading ? <Card><Spinner /></Card> : (
        <DepartmentsCard
          isApprover={isApprover}
          departments={allDepartments.data?.departments ?? []}
          roster={roster}
          onChanged={() => { allDepartments.refetch(); departments.refetch(); }}
        />
      )}
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Class → department</div>
          {classes.data && (() => {
            const cs = classes.data.classes;
            const unmappedAmt = cs.filter((c) => !c.department_id).reduce((s, c) => s + c.amount, 0);
            return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{cs.filter((c) => c.department_id).length} mapped · {cs.filter((c) => !c.department_id).length} unmapped ({fmtCurrencyWhole(unmappedAmt)})</span>;
          })()}
          <span style={{ marginLeft: 'auto' }}>
            <ActionButton onClick={() => setClassGapsOnly((v) => !v)}>{classGapsOnly ? 'Show all' : 'Only gaps'}</ActionButton>
          </span>
        </CardHeader>
        {classes.isLoading ? <Spinner />
          : !classes.data || classes.data.classes.length === 0 ? <EmptyState message="No QBO classes synced yet." />
          : (() => {
            const shown = (classGapsOnly ? classes.data.classes.filter((c) => !c.department_id) : classes.data.classes)
              .slice().sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
            if (shown.length === 0) return <EmptyState message="No gaps — every class has a department." />;
            return (
            <Table>
              <thead><tr><Th>Class</Th><Th right>Actuals (12mo)</Th><Th>Department</Th></tr></thead>
              <tbody>
                {shown.map((c) => (
                  <Tr key={c.qbo_class_id}>
                    <Td bold>{c.name}{!c.active && <Badge variant="gray">inactive</Badge>}</Td>
                    <Td right muted>{fmtCurrencyWhole(c.amount)}</Td>
                    <Td>
                      <Select
                        value={c.department_id ?? ''}
                        onChange={async (v) => { await api.fpa.setDepartmentMap(c.qbo_class_id, v === '' ? null : v); classes.refetch(); }}
                      >
                        <option value="">— unmapped —</option>
                        {(departments.data?.departments ?? []).map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </Select>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            );
          })()}
      </Card>
    </>
  );
}

function RevenueTab() {
  const revtype = useQuery(() => api.fpa.engagementTypeMap());
  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Engagement type → revenue type</div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>classifies engagements for the plan "Import from engagements" helper</span>
      </CardHeader>
      {revtype.isLoading ? <Spinner />
        : !revtype.data || revtype.data.available_types.length === 0 ? <EmptyState message="No engagement types seen yet." />
        : (
          <Table>
            <thead><tr><Th>Engagement type</Th><Th>Revenue type</Th></tr></thead>
            <tbody>
              {revtype.data.available_types.map((t) => {
                const current = revtype.data!.map.find((m) => m.engagement_type === t)?.revenue_type ?? '';
                return (
                  <Tr key={t}>
                    <Td bold>{t}</Td>
                    <Td>
                      <Select value={current} onChange={async (v) => { await api.fpa.setEngagementType(t, v === '' ? null : (v as 'TM' | 'FF' | 'SUB')); revtype.refetch(); }}>
                        <option value="">— unmapped (T&amp;M) —</option>
                        <option value="TM">T&amp;M</option>
                        <option value="FF">Fixed-Fee</option>
                        <option value="SUB">Subscription</option>
                      </Select>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
    </Card>
  );
}

export default function AdminFpaQboPage() {
  const { user } = useAuth();
  const isApprover = user?.fpa_role === 'approver';
  const [tab, setTab] = useState<QboTab>('connection');
  const status = useQuery(() => api.fpa.qbo.status());
  const s = status.data;

  return (
    <>
      <Card>
        <CardHeader>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>QuickBooks Online integration</div>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>read-only actuals source — connect, then map accounts &amp; classes onto FP&A lines and departments</span>
          </div>
          {s && (
            <Badge variant={s.connected ? 'green' : s.configured ? 'amber' : 'gray'}>
              {s.connected ? 'Connected' : s.configured ? 'Not connected' : 'Not configured'}
            </Badge>
          )}
        </CardHeader>
        <div style={{ display: 'flex', gap: 4, padding: '8px 16px', flexWrap: 'wrap' }}>
          {(Object.keys(TAB_LABELS) as QboTab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              border: '0.5px solid var(--color-border-strong)',
              background: tab === t ? 'var(--color-blue)' : 'var(--color-surface)',
              color: tab === t ? '#fff' : 'var(--color-text)',
            }}>{TAB_LABELS[t]}</button>
          ))}
        </div>
      </Card>

      {tab === 'connection' && <ConnectionTab isApprover={isApprover} />}
      {tab === 'accounts' && <AccountsTab isApprover={isApprover} />}
      {tab === 'departments' && <DepartmentsTab isApprover={isApprover} />}
      {tab === 'revenue' && <RevenueTab />}
    </>
  );
}
